// @givmo/app-charity — the getting-started strip above the Donations statement (CPA-WEB40, §9-1;
// re-homed from the retired app grid onto the class default's landing).
//
// The dissolved dashboard's completion-aware next-steps grid, re-homed as ONE dismissable band above
// the statement on `/donations` — where every charity admin lands (§9-A A1): Terms · Payments ·
// Profile, each shown only while its own read says the work remains. The Grants step is gone (the
// statement IS that door), and the enrollment door was NOT re-homed (ruling 2026-09-02).
//
// The two fail directions are deliberately opposite and unchanged from the dashboard: payment and
// profile fail CLOSED (hidden until a successful read proves work remains), Terms fails OPEN (shown
// until a successful read proves it is done) — a redundant prompt costs a click, a swallowed one
// leaves an organization unable to receive donor identities.
//
// Dismissal is a device-local hint per user and organization (`getting-started-dismissal.ts`). The
// strip disappears on its own the moment every step is done, which is the ordinary way it goes.
//
// DATA CONTRACT: `donations-getting-started` (the strip), `donations-next-terms` / `-payment` /
// `-profile` (the steps), `donations-getting-started-dismiss`. (The WIP's grid-era ids retired with
// the grid — Home is not a thing, R2.)

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  charityAdminGetCharityPaymentProfileOptions,
  charityAdminGetCharityProfileOptions,
  charityAdminGetCharityTermsAcceptanceStateOptions,
} from '@givmo/api-client/query';
import { cn, FOCUS_RING, Icon, IconButton, testIdProp } from '@givmo/design-system';
import { ArrowRight, FileText, Landmark, Store, X } from 'lucide-react';
import { useState, type ComponentType, type SVGProps } from 'react';
import { useAuth } from '../../auth/auth-context';
import { DONATIONS_PAYMENTS_ROUTE } from '../../console/console-routes';
import { dismissGettingStarted, isGettingStartedDismissed } from './getting-started-dismissal';

type StepIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

export interface GettingStartedStep {
  kind: 'terms' | 'payment' | 'profile';
  to: string;
  title: string;
  icon: StepIcon;
}

const STEPS: readonly GettingStartedStep[] = [
  { kind: 'terms', to: '/terms', title: 'Review and accept the Charity Terms', icon: FileText },
  { kind: 'payment', to: DONATIONS_PAYMENTS_ROUTE, title: 'Set up payments', icon: Landmark },
  { kind: 'profile', to: '/profile', title: 'Complete your profile', icon: Store },
];

/** The steps this organization still owes, from the three completion reads. */
export function useGettingStartedSteps(charityId: string): GettingStartedStep[] {
  const paymentQuery = useQuery({
    ...charityAdminGetCharityPaymentProfileOptions({ path: { charity_id: charityId } }),
  });
  const profileQuery = useQuery({
    ...charityAdminGetCharityProfileOptions({ path: { charity_id: charityId } }),
  });
  const termsAcceptanceQuery = useQuery({
    ...charityAdminGetCharityTermsAcceptanceStateOptions({ path: { charity_id: charityId } }),
  });

  const paymentComplete = paymentQuery.isSuccess && paymentQuery.data.profile != null;
  const loadedProfile = profileQuery.data?.profile;
  const profileComplete =
    profileQuery.isSuccess &&
    (loadedProfile?.mission?.trim().length ?? 0) > 0 &&
    (loadedProfile?.organization_domain?.trim().length ?? 0) > 0;
  // `current_version_accepted` is the whole step: `accepted` alone stays true forever once ANY
  // version was signed, so an organization owing re-acceptance after a rotation would read as done.
  const termsComplete =
    termsAcceptanceQuery.isSuccess && termsAcceptanceQuery.data.current_version_accepted === true;

  return STEPS.filter((step) => {
    if (step.kind === 'terms') return !termsComplete;
    if (step.kind === 'payment') return paymentQuery.isSuccess && !paymentComplete;
    return profileQuery.isSuccess && !profileComplete;
  });
}

export interface GettingStartedStripProps {
  charityId: string;
  /** Placement classes from the host (margins), applied to the section itself — never a wrapper. */
  className?: string;
}

export function GettingStartedStrip({ charityId, className }: GettingStartedStripProps) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const steps = useGettingStartedSteps(charityId);
  const [dismissed, setDismissed] = useState(() => isGettingStartedDismissed(uid, charityId));

  if (dismissed || steps.length === 0) return null;

  return (
    <section
      aria-label="Finish setting up"
      className={cn(
        'flex items-start gap-12 rounded-xl border border-border-subtle bg-surface py-12 pl-16 pr-8 sm:items-center',
        className,
      )}
      {...testIdProp('donations-getting-started')}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-8 sm:flex-row sm:items-center sm:gap-16">
        <p className="shrink-0 font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          Finish setting up
        </p>
        <ul className="flex min-w-0 flex-wrap gap-x-16 gap-y-8">
          {steps.map((step) => (
            <li key={step.kind}>
              <Link
                to={step.to}
                className={cn(
                  'group inline-flex items-center gap-6 rounded-md py-2 font-sans text-s13 font-w500 tracking-givmo text-brand-700 outline-none transition-colors hover:text-brand-800',
                  FOCUS_RING,
                )}
                {...testIdProp(`donations-next-${step.kind}`)}
              >
                <Icon icon={step.icon} size={16} className="text-brand-600" aria-hidden />
                {step.title}
                <Icon
                  icon={ArrowRight}
                  size={14}
                  className="transition-transform group-hover:translate-x-[2px]"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <IconButton
        aria-label="Dismiss"
        tone="ghost"
        size="sm"
        onClick={() => {
          dismissGettingStarted(uid, charityId);
          setDismissed(true);
        }}
        {...testIdProp('donations-getting-started-dismiss')}
      >
        <Icon icon={X} size={16} aria-hidden />
      </IconButton>
    </section>
  );
}
